import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { apiFetch } from '@/api/client'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'

interface Props {
  roles: string[]
  onClose: () => void
  onDone: () => void
}

interface PublishSetResponse {
  publishedSha?: string
  publishSetId: string
  versions: Array<{ role: string; version: number; sha: string }>
}

export function PublishSetFlow({ roles, onClose, onDone }: Props) {
  const [typed, setTyped] = useState('')
  const [justification, setJustification] = useState('')
  const [step, setStep] = useState<'confirm' | 'justify' | 'running' | 'done' | 'rejected'>('confirm')
  const [error, setError] = useState<string | null>(null)
  const setName = roles.join(' + ')
  const publish = useMutation({
    mutationFn: async () => {
      const r = await apiFetch<PublishSetResponse>(`/api/personas/publish`, {
        method: 'POST',
        body: JSON.stringify({ publishSet: roles, justification }),
      })
      return r.data
    },
    onSuccess: () => setStep('done'),
    onError: (e: any) => { setError(e?.error?.message ?? e?.message ?? 'failed'); setStep('rejected') },
  })

  const run = () => { setStep('running'); publish.mutate() }

  if (step === 'confirm') return (
    <Modal title={`Publish ${roles.length} together`} onClose={onClose} actions={
      <>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="primary" disabled={typed !== setName} onClick={() => setStep('justify')}>Continue</Button>
      </>
    }>
      <p>These personas publish as one set — all files are written first, then a single reload signal, so reciprocal changes land together.</p>
      <div style={{ margin: 'var(--sp-12) 0' }}>
        {roles.map((r) => <div key={r} className="mono" style={{ padding: 'var(--sp-6) 0', borderBottom: '1px solid var(--stroke-1)' }}>{r}</div>)}
      </div>
      <label>Type the set name to confirm</label>
      <input className="input mono" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={setName} autoFocus />
    </Modal>
  )

  if (step === 'justify') return (
    <Modal title="Justification required" onClose={onClose} actions={
      <>
        <Button onClick={() => setStep('confirm')}>Back</Button>
        <Button variant="primary" disabled={!justification.trim()} onClick={run}>Publish set</Button>
      </>
    }>
      <p>One shared justification applies to the whole set <b className="mono">{setName}</b> (recorded per role in the audit log).</p>
      <textarea className="input" style={{ minHeight: 90 }} value={justification} onChange={(e) => setJustification(e.target.value)}
        placeholder="e.g. FUS-430 — reciprocal Dev↔QA addressee change, land together to avoid a one-sided edge."  autoFocus />
    </Modal>
  )

  if (step === 'running') return (
    <Modal title="Publishing set…" onClose={() => {}} actions={<Button busy>Working…</Button>}>
      <p>Writing {roles.length} files atomically, signalling the bridge, polling /health…</p>
    </Modal>
  )

  if (step === 'rejected') return (
    <Modal title="Set rejected — kept prior versions" onClose={onClose} actions={<Button variant="primary" onClick={onClose}>Close</Button>}>
      <p>Validation failed on reload, so the bridge kept the prior version of <b>every</b> file in the set. No partial publish.</p>
      {error && <p style={{ color: 'var(--err)' }}>{error}</p>}
    </Modal>
  )

  return (
    <Modal title={`${setName} published`} onClose={() => { onClose(); onDone() }} actions={
      <Button variant="primary" onClick={() => { onClose(); onDone() }}>Done</Button>
    }>
      <p>All files landed in one reload.</p>
      <p className="mono">sha {publish.data?.publishedSha?.slice(0, 16)}</p>
    </Modal>
  )
}
